import { useQuery } from "@tanstack/react-query";
import { getV2ProtocolActor, hasProtocolEnv } from "@/lib/protocol-actor";

export type GasStatus = {
	label: "Healthy" | "Low" | "Critical" | "Unknown";
	opsCyclesT: number;
	protocolCyclesT: number;
	/** False when no separate ops-canister cycle API is available. */
	opsAvailable: boolean;
	source: "live" | "fixture" | "error";
	note: string;
};

/** Lightweight gas/cycles snapshot for bottom row + Cycles page. */
export function useGasStatus() {
	return useQuery({
		queryKey: ["v2", "gasStatus"],
		queryFn: async (): Promise<GasStatus> => {
			const fixture = (): GasStatus => ({
				label: "Healthy",
				opsCyclesT: 12.4,
				protocolCyclesT: 48.2,
				opsAvailable: true,
				source: "fixture",
				note: "Live cycle reads need canister env configured",
			});

			if (!hasProtocolEnv()) return fixture();
			try {
				const actor = await getV2ProtocolActor();
				if (!actor) {
					return {
						label: "Unknown",
						opsCyclesT: 0,
						protocolCyclesT: 0,
						opsAvailable: false,
						source: "error",
						note: "Cycles health query failed: protocol actor unavailable. Ops ICP below still comes from getGasFundingStatus when wired.",
					};
				}

				const h = await actor.getAutomationHealth();
				const balance = Number(h.cyclesBalance ?? 0n);
				const trap = Number(h.cyclesTrapFloor ?? 0n);
				const soft = Number(h.cyclesSoftBackstopFloor ?? 0n);
				const protocolCyclesT = balance / 1e12;

				let label: GasStatus["label"] = "Healthy";
				if (balance < trap) label = "Critical";
				else if (balance < soft || !h.softBackstopCyclesOk) label = "Low";
				else label = "Healthy";

				const protocolNote =
					protocolCyclesT < 0.01
						? `Protocol ${protocolCyclesT.toFixed(3)}T via getAutomationHealth — top-up if this stays near 0`
						: `Protocol ${protocolCyclesT.toFixed(2)}T via getAutomationHealth (T = trillion)`;

				return {
					label,
					opsCyclesT: 0,
					protocolCyclesT,
					opsAvailable: false,
					source: "live",
					note: `${protocolNote}. Ops ICP sleeve is separate (Gas Station / liquidity).`,
				};
			} catch (e) {
				const detail =
					e instanceof Error ? e.message : "cycle status query failed";
				return {
					label: "Unknown",
					opsCyclesT: 0,
					protocolCyclesT: 0,
					opsAvailable: false,
					source: "error",
					note: `Cycles health query failed (getAutomationHealth): ${detail}. Gas Station Ops ICP can still load from protocol getGasFundingStatus.`,
				};
			}
		},
		staleTime: 60_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}
